import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useLogger } from '../useLogger';
import { useUserRole } from '../useUserRole';

import { useAuthContext } from '@/context/AuthProvider';
import { UserProperty } from '@/models/Property';
import {
  editProperty,
  deleteProperty,
  getMyProperties,
  markPropertyAsFavorite,
} from '@/services/api/properties';

export const useProperties = () => {
  const queryClient = useQueryClient();
  const logger = useLogger('useProperties');
  const { user } = useAuthContext();
  const { isBuyer } = useUserRole();

  const {
    data: properties,
    refetch,
    isLoading: isInitialFetching,
    isFetching,
  } = useQuery<UserProperty[]>({
    queryKey: ['properties'],
    queryFn: getMyProperties,
    enabled: !!user && isBuyer,
  });

  const { mutate: markAsFavorite, isPending: isMarkingAsFavorite } = useMutation({
    mutationFn: (propertyId: string) => markPropertyAsFavorite(propertyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
    onError: (error) => {
      logger.error('Error marking property as favorite:', error);
    },
  });

  const {
    mutate: editPropertyMutate,
    isPending: isEditingProperty,
    isSuccess: isEditPropertySuccess,
    error: editPropertyError,
  } = useMutation({
    mutationFn: (params: { propertyId: string; name: string }) =>
      editProperty(params.propertyId, { name: params.name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
    onError: (error) => {
      logger.error('Error editing property:', error);
    },
  });

  const { mutate: deleteMutate, isPending: isDeleting } = useMutation({
    mutationFn: (propertyId: string) => deleteProperty(propertyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
    onError: (error) => {
      logger.error('Error deleting property:', error);
    },
  });

  const deletePropertyWithCallbacks = (
    propertyId: string,
    callbacks?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    },
  ) =>
    deleteMutate(propertyId, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['properties'] });
        if (callbacks?.onSuccess) {
          callbacks.onSuccess();
        }
      },
      onError: (error) => {
        if (callbacks?.onError) {
          callbacks.onError(error as Error);
        }
      },
    });

  return {
    properties,
    refetchProperties: refetch,
    isFetching,
    isInitialFetching,

    markAsFavorite,
    isMarkingAsFavorite,

    editProperty: editPropertyMutate,
    isEditingProperty,
    isEditPropertySuccess,
    editPropertyError,

    deleteProperty: deletePropertyWithCallbacks,
    isDeleting,
  };
};
